import { useReducer, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useStrategyTemplates } from '../hooks/useStrategyTemplates';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { DesktopStrategyShell } from '../components/strategy/DesktopStrategyShell';
import { MobileStrategyShell } from '../components/strategy/MobileStrategyShell';
import { DEFAULT_STRATEGY_SETTINGS, PREMADE_TEMPLATES } from '../constants/premadeTemplates';
import type {
  StrategyFormState, StrategyAction, StrategySettings, StrategyTabKey, DesktopStrategyTabKey,
  StrategyTemplate, ActiveStrategyReference,
} from '../types/strategy';
import type { Province } from '../types/province';
import { COLORS } from '../constants/theme';

/**
 * SF-172: named, fully-typed props shared by MobileStrategyShell/DesktopStrategyShell — same
 * PoliticsShellProps/MagicShellProps/ExploreShellProps precedent (SF-139/SF-148/SF-152). State
 * stays hoisted here (not per-shell) so a mid-edit breakpoint flip doesn't discard in-progress
 * form state, per CLAUDE.md's standing rule.
 */
export interface StrategyShellProps {
  province: Province;
  formState: StrategyFormState;
  dispatch: React.Dispatch<StrategyAction>;
  // Tab state — two independent state slots (SF-141 Training precedent), not a shared enum +
  // defensive reset effect. DesktopStrategyShell only ever reads/writes desktopActiveTab;
  // MobileStrategyShell only ever reads/writes mobileTab. Neither shell can put the other's state
  // into an invalid value.
  desktopActiveTab: DesktopStrategyTabKey;
  setDesktopActiveTab: (tab: DesktopStrategyTabKey) => void;
  mobileTab: StrategyTabKey;
  setMobileTab: (tab: StrategyTabKey) => void;
  totalLand: number;
  population: number;
  race: string | undefined;
  existingBooks: Record<string, number>;
  templates: StrategyTemplate[];
  activeStrategyRef: ActiveStrategyReference | null;
  templatesLoading: boolean;
  templatesError: string | null;
  isDirty: boolean;
  currentTemplateId: string | null;
  /** SF-141-precedented stale-draft hazard: incremented by Strategy.tsx inside handleLoadTemplate
   *  (the sole live dispatcher of LOAD_TEMPLATE), threaded down as NumericInput's `resetKey` so a
   *  mid-edit field doesn't keep showing stale typed text after a template load. */
  bulkMutationVersion: number;
  handleLoadTemplate: (settings: StrategySettings) => void;
  handleActivateCustom: (strategyId: string) => Promise<void>;
  handleActivatePremade: (premadeKey: string) => Promise<void>;
  handleDeactivate: () => Promise<void>;
  handleSaveNew: (name: string) => Promise<void>;
  handleSaveChanges: (strategyId: string) => Promise<void>;
  handleRename: (strategyId: string, newName: string) => Promise<void>;
  handleDelete: (strategyId: string) => Promise<void>;
  /** Re-invokes loadTemplates() — powers the Retry action on the page-level templatesError banner. */
  onRetryLoadTemplates: () => void;
}

function strategyReducer(state: StrategyFormState, action: StrategyAction): StrategyFormState {
  switch (action.type) {
    case 'SET_BUILDING':
      return { ...state, buildings: { ...state.buildings, [action.building]: action.value } };
    case 'SET_DRAFT_LEVEL':
      return { ...state, draftLevel: action.value };
    case 'SET_DRAFT_RATE':
      return { ...state, draftRate: action.value };
    case 'SET_WAGE_RATE':
      return { ...state, wageRate: action.value };
    case 'SET_SCIENCE_RATE':
      return { ...state, scienceRate: action.value };
    case 'SET_SCIENCE_ALLOC':
      return { ...state, scienceAlloc: { ...state.scienceAlloc, [action.branch]: action.value } };
    case 'SET_ARMY_COMP':
      return { ...state, armyComp: { ...state.armyComp, [action.unit]: action.value } };
    case 'SET_THIEF_PA':
      return { ...state, thiefPA: action.value };
    case 'SET_WIZARD_PA':
      return { ...state, wizardPA: action.value };
    case 'LOAD_TEMPLATE':
      return settingsToFormState(action.settings);
    case 'RESET':
      return settingsToFormState(DEFAULT_STRATEGY_SETTINGS);
    default:
      return state;
  }
}

function settingsToFormState(settings: StrategySettings): StrategyFormState {
  return {
    buildings: { ...settings.buildings },
    draftLevel: settings.draftLevel,
    draftRate: settings.draftRate,
    wageRate: settings.wageRate,
    scienceRate: settings.scienceRate,
    scienceAlloc: { ...settings.scienceAlloc },
    armyComp: { ...settings.armyComp },
    thiefPA: settings.thiefPA,
    wizardPA: settings.wizardPA,
  };
}

function formStateToSettings(state: StrategyFormState): StrategySettings {
  return {
    buildings: state.buildings,
    draftLevel: state.draftLevel,
    draftRate: state.draftRate,
    wageRate: state.wageRate,
    scienceRate: state.scienceRate,
    scienceAlloc: state.scienceAlloc,
    armyComp: state.armyComp,
    thiefPA: state.thiefPA,
    wizardPA: state.wizardPA,
  };
}

const INITIAL_STATE = settingsToFormState(DEFAULT_STRATEGY_SETTINGS);

export function Strategy() {
  const platform = useAppShellPlatform();
  const { provinceId, worldId } = useAuth();
  const { province, isLoading: provinceLoading, error: provinceError } = useProvince();
  const {
    templates, activeStrategyRef, isLoading: templatesLoading, error: templatesError,
    loadTemplates, createTemplate, updateTemplate, deleteTemplate,
    setActiveStrategyCustom, setActiveStrategyPremade, deactivateStrategy,
  } = useStrategyTemplates(worldId, provinceId);

  const [formState, dispatch] = useReducer(strategyReducer, INITIAL_STATE);
  const [desktopActiveTab, setDesktopActiveTab] = useState<DesktopStrategyTabKey>('economy');
  const [mobileTab, setMobileTab] = useState<StrategyTabKey>('economy');
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // SF-172: bumped inside handleLoadTemplate, threaded down as NumericInput's `resetKey` so a
  // mid-edit field doesn't keep showing stale typed text after a template load.
  const [bulkMutationVersion, setBulkMutationVersion] = useState(0);
  const loadedRef = useRef(false);
  const lastSettingsJson = useRef<string>('');

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Track dirty state
  useEffect(() => {
    const json = JSON.stringify(formStateToSettings(formState));
    if (lastSettingsJson.current && json !== lastSettingsJson.current) {
      setIsDirty(true);
    }
  }, [formState]);

  // Load active strategy on initial template load
  useEffect(() => {
    if (loadedRef.current || templatesLoading) return;
    if (activeStrategyRef) {
      // Find matching premade or custom template to load
      if (activeStrategyRef.sourceType === 'custom') {
        const t = templates.find((t) => t.id === activeStrategyRef.sourceId);
        if (t) {
          dispatch({ type: 'LOAD_TEMPLATE', settings: t.settings });
          setCurrentTemplateId(t.id);
          lastSettingsJson.current = JSON.stringify(t.settings);
          setIsDirty(false);
        }
      } else if (activeStrategyRef.sourceType === 'premade') {
        const p = PREMADE_TEMPLATES.find((t) => t.key === activeStrategyRef.sourceId);
        if (p) {
          dispatch({ type: 'LOAD_TEMPLATE', settings: p.settings });
          lastSettingsJson.current = JSON.stringify(p.settings);
          setIsDirty(false);
        }
        setCurrentTemplateId(null);
      }
      loadedRef.current = true;
    } else if (templates.length > 0 || !templatesLoading) {
      loadedRef.current = true;
    }
  }, [templates, activeStrategyRef, templatesLoading]);

  const handleLoadTemplate = useCallback((settings: StrategySettings) => {
    dispatch({ type: 'LOAD_TEMPLATE', settings });
    lastSettingsJson.current = JSON.stringify(settings);
    setIsDirty(false);
    setBulkMutationVersion((v) => v + 1);
  }, []);

  const handleActivateCustom = useCallback(async (strategyId: string) => {
    await setActiveStrategyCustom(strategyId);
    setCurrentTemplateId(strategyId);
  }, [setActiveStrategyCustom]);

  const handleActivatePremade = useCallback(async (premadeKey: string) => {
    await setActiveStrategyPremade(premadeKey);
    setCurrentTemplateId(null);
  }, [setActiveStrategyPremade]);

  const handleDeactivate = useCallback(async () => {
    await deactivateStrategy();
    setCurrentTemplateId(null);
  }, [deactivateStrategy]);

  const handleSaveNew = useCallback(async (name: string) => {
    const t = await createTemplate({ name, settings: formStateToSettings(formState) });
    setCurrentTemplateId(t.id);
    lastSettingsJson.current = JSON.stringify(formStateToSettings(formState));
    setIsDirty(false);
    await setActiveStrategyCustom(t.id);
  }, [createTemplate, formState, setActiveStrategyCustom]);

  const handleSaveChanges = useCallback(async (strategyId: string) => {
    await updateTemplate(strategyId, { settings: formStateToSettings(formState) });
    lastSettingsJson.current = JSON.stringify(formStateToSettings(formState));
    setIsDirty(false);
  }, [updateTemplate, formState]);

  const handleRename = useCallback(async (strategyId: string, newName: string) => {
    await updateTemplate(strategyId, { name: newName });
  }, [updateTemplate]);

  const handleDelete = useCallback(async (strategyId: string) => {
    await deleteTemplate(strategyId);
    if (currentTemplateId === strategyId) {
      setCurrentTemplateId(null);
    }
  }, [deleteTemplate, currentTemplateId]);

  if (provinceLoading) {
    return (
      <div
        className="flex justify-center items-center min-h-screen"
        style={{ backgroundColor: COLORS.bg }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (provinceError || !province) {
    return (
      <div className="min-h-screen px-4 py-8" style={{ backgroundColor: COLORS.bg }}>
        <ErrorMessage message={provinceError || 'Failed to load province'} />
      </div>
    );
  }

  const totalLand = province.territory.totals.acres;
  const population = province.population.total;
  const race = province.metadata.race;
  const existingBooks: Record<string, number> = province.sciences?.books ?? {};

  const shellProps: StrategyShellProps = {
    province, formState, dispatch, desktopActiveTab, setDesktopActiveTab, mobileTab, setMobileTab,
    totalLand, population, race, existingBooks,
    templates, activeStrategyRef, templatesLoading, templatesError, isDirty, currentTemplateId,
    bulkMutationVersion,
    handleLoadTemplate, handleActivateCustom, handleActivatePremade, handleDeactivate,
    handleSaveNew, handleSaveChanges, handleRename, handleDelete, onRetryLoadTemplates: loadTemplates,
  };

  return (
    <div>
      {templatesError && (
        <div
          className="px-4 py-2 rounded text-sm mb-4 flex items-center justify-between gap-3"
          style={{
            backgroundColor: COLORS.errorBg,
            border: `1px solid ${COLORS.errorBorder}`,
            color: COLORS.errorText,
          }}
        >
          <span>{templatesError}</span>
          <button
            onClick={loadTemplates}
            className="text-xs font-medium underline flex-shrink-0"
            style={{ color: COLORS.errorText }}
          >
            Retry
          </button>
        </div>
      )}

      {platform === 'desktop'
        ? <DesktopStrategyShell {...shellProps} />
        : <MobileStrategyShell {...shellProps} />}
    </div>
  );
}
